/**
 * Product card gallery: alt-based color filtering + CSS-transform swipe.
 * Supports touch + mouse (pointer) drag, arrows, and color swatches.
 * Wrap-around (last→first / first→last) animates via a temporary clone slide.
 */
(function () {
  if (window.__productCardGalleryBound) return;
  window.__productCardGalleryBound = true;

  var SWIPE_THRESHOLD = 36;
  var SLIDE_MS = 500;
  // Commit to next/prev once the drag covers half the card width.
  var SWIPE_COMMIT_RATIO = 0.5;

  function findParentSwiper(el) {
    var node = el.parentElement;
    while (node) {
      if (node.swiper) return node.swiper;
      node = node.parentElement;
    }
    return null;
  }

  function initCard(card) {
    if (!card || card.getAttribute('data-card-gallery-ready') === 'true') return;
    card.setAttribute('data-card-gallery-ready', 'true');

    var pictures = Array.prototype.slice.call(
      card.querySelectorAll('.product-card__picture[data-slide]')
    );
    if (!pictures.length) return;

    var currentColor = card.getAttribute('data-default-color') || '';
    var currentSlide = 0;
    // When true, hover peek is disabled — user chose a slide via arrows/swipe.
    var pinnedByUser = false;
    var isHovering = false;
    // Hover already revealed the next gallery image (usually slide 1) — skip it
    // on the first next/swipe so that image is not shown twice in a row.
    var hoverRevealedNext = false;
    var animatingWrap = false;
    var priceEl = card.querySelector('[data-product-card-price]');
    var compareEl = card.querySelector('[data-product-card-compare]');
    var swatches = Array.prototype.slice.call(
      card.querySelectorAll('[data-product-card-swatch]')
    );
    var prevBtn = card.querySelector('[data-product-card-prev]');
    var nextBtn = card.querySelector('[data-product-card-next]');
    var controls = card.querySelector('.product-card__media-controls');
    var media = card.querySelector('.product-card__media');
    var track =
      card.querySelector('[data-product-card-track]') ||
      card.querySelector('.product-card__gallery-track');

    var canHoverPeek =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    function slidesForColor(color) {
      return pictures.filter(function (picture) {
        return (picture.getAttribute('data-color') || '') === color;
      });
    }

    function activeSlides() {
      var matched = slidesForColor(currentColor);
      return matched.length ? matched : pictures;
    }

    function syncControls() {
      if (!controls) return;
      var multi = activeSlides().length > 1;
      controls.hidden = !multi;
      controls.setAttribute('aria-hidden', multi ? 'false' : 'true');
    }

    function setTrackSlide(index, instant) {
      if (!track) return;
      if (instant) {
        track.classList.add('is-instant');
        track.classList.remove('is-animating');
      } else {
        track.classList.add('is-animating');
      }
      track.style.setProperty('--slide', String(index));
      if (instant) {
        void track.offsetWidth;
        track.classList.remove('is-instant');
      } else {
        var settled = false;
        function clearAnimating() {
          if (settled) return;
          settled = true;
          track.classList.remove('is-animating');
          track.removeEventListener('transitionend', onTransitionEnd);
          if (timeoutId !== null) window.clearTimeout(timeoutId);
        }
        function onTransitionEnd(event) {
          if (event.target !== track) return;
          if (event.propertyName && event.propertyName !== 'transform') return;
          clearAnimating();
        }
        track.addEventListener('transitionend', onTransitionEnd);
        var timeoutId = window.setTimeout(clearAnimating, SLIDE_MS + 80);
      }
    }

    function syncVisibleSlides() {
      var slides = activeSlides();

      pictures.forEach(function (picture) {
        var inGallery = slides.indexOf(picture) !== -1;
        picture.hidden = !inGallery;
        picture.classList.remove('is-active');
        picture.setAttribute('aria-hidden', inGallery ? 'false' : 'true');
      });

      return slides;
    }

    function markActive(slides, index) {
      slides.forEach(function (picture, i) {
        picture.classList.toggle('is-active', i === index);
      });
    }

    function finishWrap(clone, settleIndex, slides) {
      if (clone && clone.parentNode) {
        clone.parentNode.removeChild(clone);
      }
      currentSlide = settleIndex;
      markActive(slides, currentSlide);
      setTrackSlide(currentSlide, true);
      animatingWrap = false;
      syncControls();
    }

    function animateWrap(forward, slides, settleIndex) {
      if (!track || slides.length < 2) {
        currentSlide = settleIndex;
        markActive(slides, currentSlide);
        setTrackSlide(currentSlide, true);
        syncControls();
        return;
      }

      animatingWrap = true;
      var source = forward ? slides[0] : slides[slides.length - 1];
      var clone = source.cloneNode(true);
      clone.removeAttribute('data-slide');
      clone.removeAttribute('hidden');
      clone.hidden = false;
      clone.classList.add('product-card__picture--clone');
      clone.classList.remove('is-active');
      clone.setAttribute('aria-hidden', 'true');

      var settled = false;
      var timeoutId = null;

      function settle() {
        if (settled) return;
        settled = true;
        track.removeEventListener('transitionend', onTransitionEnd);
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
        finishWrap(clone, settleIndex, slides);
      }

      function onTransitionEnd(event) {
        if (event.target !== track) return;
        if (event.propertyName && event.propertyName !== 'transform') return;
        settle();
      }

      track.addEventListener('transitionend', onTransitionEnd);
      timeoutId = window.setTimeout(settle, SLIDE_MS + 80);

      if (forward) {
        track.appendChild(clone);
        currentSlide = settleIndex;
        markActive(slides, currentSlide);
        // Next frame so the browser paints the clone before animating to it.
        requestAnimationFrame(function () {
          setTrackSlide(slides.length, false);
        });
      } else {
        track.insertBefore(clone, slides[0]);
        // Prepend shifts every slide +1; stay on the same image instantly.
        setTrackSlide(1, true);
        currentSlide = settleIndex;
        markActive(slides, currentSlide);
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            setTrackSlide(0, false);
          });
        });
      }
    }

    function show(slideIndex, options) {
      options = options || {};
      if (animatingWrap && !options.instant && !options.reset) return;

      var slides = syncVisibleSlides();
      if (!slides.length) return;

      var requested = slideIndex;
      var nextIndex = ((slideIndex % slides.length) + slides.length) % slides.length;
      var previousIndex = currentSlide;
      var len = slides.length;

      var wrapForward =
        !options.instant &&
        !options.reset &&
        len > 1 &&
        previousIndex === len - 1 &&
        nextIndex === 0 &&
        requested > previousIndex;

      var wrapBackward =
        !options.instant &&
        !options.reset &&
        len > 1 &&
        previousIndex === 0 &&
        nextIndex === len - 1 &&
        requested < previousIndex;

      if (wrapForward || wrapBackward) {
        animateWrap(wrapForward, slides, nextIndex);
        return;
      }

      currentSlide = nextIndex;
      markActive(slides, currentSlide);
      setTrackSlide(currentSlide, Boolean(options.instant || options.reset));
      syncControls();
    }

    function hoverAltPicture(slides) {
      // Prefer the active color's second gallery slide so hover always
      // matches the selected swatch (never bleed default/gold media).
      if (slides && slides.length > 1) return slides[1];

      // Fallback: dedicated secondary only when it matches current color.
      // Never fall back to another color's secondary (that showed gold on black).
      var secondaries = card.querySelectorAll('[data-product-card-hover-secondary]');
      for (var i = 0; i < secondaries.length; i++) {
        if ((secondaries[i].getAttribute('data-color') || '') === currentColor) {
          return secondaries[i];
        }
      }
      return null;
    }

    function clearHoverAltStyles() {
      pictures.forEach(function (picture) {
        picture.classList.remove('is-hover-alt');
        picture.style.removeProperty('width');
        picture.style.removeProperty('height');
        picture.style.removeProperty('left');
        picture.style.removeProperty('top');
      });
      Array.prototype.forEach.call(
        card.querySelectorAll('[data-product-card-hover-secondary]'),
        function (secondary) {
          secondary.classList.remove('is-hover-alt');
          secondary.style.removeProperty('width');
          secondary.style.removeProperty('height');
          secondary.style.removeProperty('left');
          secondary.style.removeProperty('top');
        }
      );
    }

    function applyHoverPeek() {
      if (!canHoverPeek || pinnedByUser || animatingWrap || !media) return;
      var slides = activeSlides();
      var alt = hoverAltPicture(slides);
      // Need either a control-style secondary, or at least 2 gallery slides.
      if (!alt) return;
      if (!alt.hasAttribute('data-product-card-hover-secondary') && slides.length < 2) {
        return;
      }
      var wrap = card.querySelector('.product-card__image-wrap');
      var w = (wrap && wrap.clientWidth) || media.clientWidth || 0;
      var h = (wrap && wrap.clientHeight) || media.clientHeight || 0;
      if (w < 1 || h < 1) return;

      // Fade to the hover image — do not slide the track.
      clearHoverAltStyles();

      // Dedicated secondary sits over the wrap (not in the translated track).
      // Legacy fallback (gallery slide 2) still offsets into the track.
      alt.style.width = w + 'px';
      alt.style.height = h + 'px';
      if (alt.hasAttribute('data-product-card-hover-secondary')) {
        alt.style.left = '0';
      } else {
        alt.style.left = currentSlide * w + 'px';
      }
      alt.style.top = '0';
      alt.classList.add('is-hover-alt');
      media.classList.add('is-hover-fade');
      // Peek shows the upcoming image while still on slide 0.
      hoverRevealedNext = currentSlide === 0 && slides.length > 1;
    }

    function clearHoverPeek() {
      if (!canHoverPeek || pinnedByUser || animatingWrap || !media) return;
      media.classList.remove('is-hover-fade');
      window.setTimeout(function () {
        if (isHovering) return;
        clearHoverAltStyles();
      }, 1500);
    }

    function markPinned() {
      pinnedByUser = true;
      if (media) {
        media.classList.remove('is-hover-fade');
        clearHoverAltStyles();
      }
    }

    function slideImageKey(picture) {
      if (!picture) return '';
      var img = picture.querySelector('img');
      if (!img) return '';
      var raw =
        img.currentSrc ||
        img.src ||
        img.getAttribute('src') ||
        img.getAttribute('data-src') ||
        '';
      if (!raw && img.getAttribute('srcset')) {
        raw = String(img.getAttribute('srcset')).split(',')[0].trim().split(' ')[0];
      }
      return String(raw).split('?')[0].split('/').pop();
    }

    function advanceFromUser(direction) {
      var slides = activeSlides();
      var target = currentSlide + direction;
      // Hover already showed slide 1 — jump to slide 2 so it is not repeated
      // on every product card. Flag + live hover-alt check both count (users
      // often leave the image for the arrows before clicking next).
      if (direction > 0 && currentSlide === 0 && slides.length > 2) {
        var hoverAlt = card.querySelector('.is-hover-alt');
        var hoverMatchesNext =
          hoverAlt &&
          slideImageKey(hoverAlt) &&
          slideImageKey(hoverAlt) === slideImageKey(slides[1]);
        if (hoverRevealedNext || isHovering || hoverMatchesNext) {
          target = 2;
        }
      }
      hoverRevealedNext = false;
      markPinned();
      show(target);
    }

    function setColor(color, price, compare, swatchBtn) {
      currentColor = color || '';
      currentSlide = 0;
      pinnedByUser = false;
      hoverRevealedNext = false;
      animatingWrap = false;

      if (track) {
        Array.prototype.forEach.call(
          track.querySelectorAll('.product-card__picture--clone'),
          function (node) {
            if (node.parentNode) node.parentNode.removeChild(node);
          }
        );
      }

      swatches.forEach(function (btn) {
        var on = btn === swatchBtn;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        var visual = btn.querySelector('.product-card__swatch');
        if (visual) {
          visual.classList.remove('product-card__swatch--active');
        }
      });

      if (priceEl && price) {
        priceEl.textContent = price;
      }

      if (compareEl) {
        if (compare) {
          compareEl.textContent = compare;
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }

      show(0, { reset: true });
      if (isHovering) {
        applyHoverPeek();
      }
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (animatingWrap) return;
        hoverRevealedNext = false;
        markPinned();
        show(currentSlide - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (animatingWrap) return;
        advanceFromUser(1);
      });
    }

    swatches.forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        setColor(
          btn.getAttribute('data-color') || '',
          btn.getAttribute('data-price') || '',
          btn.getAttribute('data-compare') || '',
          btn
        );
      });
    });

    if (media && canHoverPeek) {
      media.addEventListener('mouseenter', function () {
        isHovering = true;
        applyHoverPeek();
      });
      media.addEventListener('mouseleave', function () {
        isHovering = false;
        // Keep hoverRevealedNext until next/prev/swipe/color change.
        // Users leave the image for arrows/title before clicking next; clearing
        // on leave made slide 1 repeat after hover on most cards.
        clearHoverPeek();
      });
    }

    if (media) {
      var pointerId = null;
      var startX = 0;
      var startY = 0;
      var axis = null;
      var dragOffset = 0;
      var lockedParent = null;
      var didDrag = false;

      function unlockParent() {
        if (lockedParent) {
          lockedParent.allowTouchMove = true;
          lockedParent = null;
        }
      }

      function clearDrag() {
        if (!track) return;
        track.style.removeProperty('--drag');
        track.classList.remove('is-dragging');
      }

      // Kill native image-drag ghost (looks like a stuck swipe overlay).
      media.addEventListener('dragstart', function (event) {
        event.preventDefault();
      });

      function prefersCarouselSwipe() {
        // Inside a featured-collection carousel on touch/mobile, horizontal
        // gestures must move the product rail — not the in-card gallery.
        // Support both Swiper (node.swiper) and the native [data-featured-swiper]
        // JazzUpCarousel used by Bestsellers / New Arrivals.
        var inNativeCarousel = !!(media && media.closest('[data-featured-swiper]'));
        if (!inNativeCarousel && !findParentSwiper(media)) return false;
        // Embla rails take drags from mouse and touch alike (in-card arrows still work).
        if (media.closest('[data-embla-ready]')) return true;
        try {
          if (window.matchMedia('(pointer: coarse)').matches) return true;
          if (window.matchMedia('(max-width: 768px)').matches) return true;
        } catch (e) {
          /* ignore */
        }
        return false;
      }

      function onPointerDown(event) {
        if (animatingWrap) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (activeSlides().length < 2) return;
        if (prefersCarouselSwipe()) return;
        if (
          event.target.closest(
            '[data-product-card-prev], [data-product-card-next], .product-card__quick-add'
          )
        ) {
          return;
        }

        pointerId = event.pointerId;
        axis = null;
        dragOffset = 0;
        didDrag = false;
        startX = event.clientX;
        startY = event.clientY;

        // Do NOT setPointerCapture here. Capturing on .product-card__media
        // retargets the click away from <a.product-card__image-link> and
        // blocks PDP navigation. Capture only after a horizontal swipe starts.
      }

      function onPointerMove(event) {
        if (pointerId === null || event.pointerId !== pointerId) return;

        var dx = event.clientX - startX;
        var dy = event.clientY - startY;

        if (!axis) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';

          if (axis === 'y') {
            unlockParent();
            clearDrag();
            pointerId = null;
            return;
          }

          lockedParent = findParentSwiper(media);
          if (lockedParent) {
            lockedParent.allowTouchMove = false;
          }
          try {
            media.setPointerCapture(pointerId);
          } catch (e) {
            /* ignore */
          }
          if (track) {
            track.classList.add('is-dragging');
          }
        }

        if (axis === 'x') {
          dragOffset = dx;
          if (Math.abs(dx) >= 10) {
            didDrag = true;
          }
          if (track) {
            var width = media.clientWidth || 1;
            // CSS is (slide + drag) * -100%. Dragging left (dx < 0) must increase
            // drag so the track moves left and the next image peeks in.
            var drag = -dx / width;
            var slides = activeSlides();
            var atStart = currentSlide === 0;
            var atEnd = currentSlide === slides.length - 1;
            // Soft rubber-band only when there is nowhere to wrap visually mid-drag
            // (wrap still commits on release).
            if ((atStart && dx > 0) || (atEnd && dx < 0)) {
              drag = drag * 0.35;
            }
            track.style.setProperty('--drag', String(drag));
          }
          if (event.cancelable) {
            event.preventDefault();
          }
        }
      }

      function onPointerUp(event) {
        if (pointerId === null || event.pointerId !== pointerId) return;

        var dx = event.clientX - startX;
        var dy = event.clientY - startY;
        pointerId = null;

        var width = media.clientWidth || 1;
        var commitDistance = Math.max(width * SWIPE_COMMIT_RATIO, SWIPE_THRESHOLD);
        var shouldCommit =
          axis === 'x' && Math.abs(dx) >= commitDistance && Math.abs(dx) > Math.abs(dy);

        if (shouldCommit) {
          // Clear drag while still in is-dragging (no transition) so we don't
          // animate backward before advancing to the target slide.
          if (track) {
            track.style.removeProperty('--drag');
          }
          if (dx < 0) {
            advanceFromUser(1);
          } else {
            hoverRevealedNext = false;
            markPinned();
            show(currentSlide - 1);
          }
          if (track) {
            track.classList.remove('is-dragging');
          }
        } else {
          // Release transition: ease back to the current slide.
          if (track) {
            track.classList.remove('is-dragging');
            track.style.removeProperty('--drag');
          }
          setTrackSlide(currentSlide, false);
        }

        unlockParent();
        axis = null;
        dragOffset = 0;
      }

      function onPointerCancel(event) {
        if (pointerId === null || event.pointerId !== pointerId) return;
        pointerId = null;
        clearDrag();
        unlockParent();
        axis = null;
        dragOffset = 0;
        setTrackSlide(currentSlide, false);
      }

      media.addEventListener('pointerdown', onPointerDown);
      media.addEventListener('pointermove', onPointerMove, { passive: false });
      media.addEventListener('pointerup', onPointerUp);
      media.addEventListener('pointercancel', onPointerCancel);
      media.addEventListener('lostpointercapture', onPointerCancel);

      // After a horizontal drag, block the image-link click so we don't navigate away.
      // Plain clicks must open the product PDP.
      media.addEventListener(
        'click',
        function (event) {
          if (
            event.target.closest(
              '[data-product-card-prev], [data-product-card-next], .product-card__quick-add'
            )
          ) {
            return;
          }

          if (didDrag) {
            didDrag = false;
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          var link = media.querySelector('a.product-card__image-link');
          if (!link) return;
          // BYOB / opt-in cards open Quick View from the image — leave those alone.
          if (link.hasAttribute('data-quick-add-open')) return;

          var href = link.href;
          if (!href) return;

          // Modifier clicks: let the browser handle new-tab / download behavior.
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          if (typeof event.button === 'number' && event.button !== 0) return;

          // Force navigation. Gallery pointer handling can prevent the native
          // <a> default even when didDrag is false.
          event.preventDefault();
          event.stopPropagation();
          window.location.assign(href);
        },
        true
      );
    }

    var initialSwatch =
      swatches.find(function (btn) {
        return (btn.getAttribute('data-color') || '') === currentColor;
      }) || swatches[0];

    if (initialSwatch) {
      setColor(
        initialSwatch.getAttribute('data-color') || currentColor,
        initialSwatch.getAttribute('data-price') || '',
        initialSwatch.getAttribute('data-compare') || '',
        initialSwatch
      );
    } else {
      show(0, { instant: true });
    }
  }

  function scan(root) {
    (root || document).querySelectorAll('[data-product-card]').forEach(initCard);
  }

  function normalizeColor(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/matte|ceramic|color|colour/g, ' ')
      .replace(/grey/g, 'gray')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function colorsMatch(a, b) {
    var left = normalizeColor(a);
    var right = normalizeColor(b);
    if (!left || !right) return false;
    if (left === right) return true;
    if (left.indexOf(right) !== -1 || right.indexOf(left) !== -1) return true;
    var leftParts = left.split(' ');
    var rightParts = right.split(' ');
    return leftParts.some(function (part) {
      return part.length > 2 && rightParts.indexOf(part) !== -1;
    });
  }

  function selectColorOnCard(card, color) {
    if (!card || !color) return false;
    var swatches = Array.prototype.slice.call(card.querySelectorAll('[data-product-card-swatch]'));
    if (!swatches.length) return false;
    var match =
      swatches.find(function (btn) {
        return colorsMatch(btn.getAttribute('data-color') || '', color);
      }) || null;
    if (!match) return false;
    match.click();
    return true;
  }

  function selectProductCardColor(color) {
    var selected = 0;
    document.querySelectorAll('[data-product-card]').forEach(function (card) {
      if (selectColorOnCard(card, color)) selected += 1;
    });
    return selected;
  }

  function bindHeroSwatches(root) {
    (root || document).querySelectorAll('[data-collection-hero-swatches]').forEach(function (group) {
      var buttons = Array.prototype.slice.call(group.querySelectorAll('[data-collection-hero-swatch]'));
      if (!buttons.length) return;

      buttons.forEach(function (btn) {
        if (btn.dataset.heroSwatchReady === '1') return;
        btn.dataset.heroSwatchReady = '1';
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          var color = btn.getAttribute('data-color') || btn.getAttribute('title') || '';
          buttons.forEach(function (el) {
            el.classList.toggle('is-active', el === btn);
            el.setAttribute('aria-pressed', el === btn ? 'true' : 'false');
          });
          selectProductCardColor(color);
        });
      });

      // Ensure the auto-selected first swatch shows its ring and syncs cards.
      var active =
        buttons.find(function (btn) {
          return btn.classList.contains('is-active');
        }) || buttons[0];
      if (active) {
        buttons.forEach(function (el) {
          var on = el === active;
          el.classList.toggle('is-active', on);
          el.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        selectProductCardColor(active.getAttribute('data-color') || active.getAttribute('title') || '');
      }
    });
  }

  function initCardFadeIns(root) {
    var cards = Array.prototype.slice.call(
      (root || document).querySelectorAll('[data-product-card-fade].product-card--fade-in')
    );
    if (!cards.length) return;

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      cards.forEach(function (card) {
        card.classList.add('is-inview');
      });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      cards.forEach(function (card) {
        card.classList.add('is-inview');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-inview');
          observer.unobserve(entry.target);
        });
      },
      { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    cards.forEach(function (card) {
      if (card.classList.contains('is-inview')) return;
      observer.observe(card);
    });
  }

  window.initProductCardGalleries = scan;
  window.selectProductCardColor = selectProductCardColor;

  function boot(root) {
    scan(root || document);
    bindHeroSwatches(root || document);
    initCardFadeIns(root || document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target || document);
  });
})();
